import {
  useCallback,
  useEffect,
  useEffectEvent,
  useMemo,
  useRef,
  useState,
} from "react";
import { TaskType } from "@coworker/apprestructured/src/task-management/types/TaskType";
import styles from "./TaskCreatorSheet.module.css";
import {
  CreatorFragmentCommonProps,
  CreatorFragmentHandle,
} from "@coworker/apprestructured/src/task-management/components/TaskCreatorAndEditorSheet/creator-fragments/CreatorFragment";
import {
  CreateTask,
  Task,
} from "@coworker/apprestructured/src/task-management/types/Task";
import { FixaModal } from "@coworker/apprestructured/src/shared/wrappers/FixaModal/FixaModal";
import { FixaSheets } from "@coworker/apprestructured/src/shared/wrappers/FixaModal/FixaSheets";
import useSearchParamState from "@coworker/apprestructured/src/shared/hooks/useSearchParamState";
import useSaveGuard from "@coworker/apprestructured/src/save-guard/useSaveGuard";
import { useTranslation } from "@coworker/locales";
import { useCreateTask } from "@coworker/apprestructured/src/task-management/services/useCreateTask";
import { useAllTaskTypes } from "@coworker/apprestructured/src/task-management/services/useTaskTypes";
import { LOADING } from "@coworker/apprestructured/src/task-management/loading";
import { merge } from "@coworker/apprestructured/src/shared/helpers/object.helper";
import CreateTaskSchedule, {
  CreateTaskScheduleChangeEvent,
} from "@coworker/apprestructured/src/task-management/components/ScheduleTask/CreateTaskSchedule/CreateTaskSchedule";
import { useImageUploader } from "@coworker/apprestructured/src/uploader/useImageUploader";
import { useFileUploader } from "@coworker/apprestructured/src/uploader/useFileUploader";
import {
  CreateSchedule,
  Recurrence,
} from "@coworker/apprestructured/src/task-management/types/Schedule";
import { useQueryClient } from "@tanstack/react-query";
import { invalidateTaskQueries } from "@coworker/apprestructured/src/task-management/services/queryKeyHelper";
import { reportMessageToSentry } from "@coworker/app/src/hooks/useConfigureSentry";
import { useCreateSchedule } from "@coworker/apprestructured/src/task-management/services/useCreateSchedule";
import { NewOrExistingFile } from "@coworker/apprestructured/src/shared/types/newOrExistingFile";
import SomethingWentWrongPrompt from "../../SomethingWentWrongPrompt/SomethingWentWrongPrompt";
import { TaskSheetHeader } from "@coworker/apprestructured/src/task-management/components/TaskCreatorAndEditorSheet/TaskSheetHeader/TaskSheetHeader";
import { TaskCreatorAndEditorSheetFooter } from "@coworker/apprestructured/src/task-management/components/TaskCreatorAndEditorSheet/TaskCreatorAndEditorSheetFooter/TaskCreatorAndEditorSheetFooter";
import useCreateInitialTask from "@coworker/apprestructured/src/task-management/services/useCreateInitialTask";
import { TaskCreatorAndEditorSheetContent } from "@coworker/apprestructured/src/task-management/components/TaskCreatorAndEditorSheet/TaskCreatorAndEditorSheetContent/TaskCreatorAndEditorSheetContent";

/**
 * The TaskCreatorSheet is open for the task type with id 'taskCreator' taken
 * from the url search params. Open/close the sheet by adding/removing a
 * 'taskCreator' search param, or use the 'useSearchParamState' hook to reduce
 * some boilerplate.
 */
function TaskCreatorSheet() {
  const [{ taskCreator: taskTypeId }, setSearchParams] = useSearchParamState();
  const [taskType, setTaskType] = useState<TaskType | null>(null);
  const [task, setTask] = useState<Record<string, unknown> | null>(null);
  const [taskTypeNotFound, setTaskTypeNotFound] = useState(false);
  const [showValidation, setShowValidation] = useState(false);
  const [images, setImages] = useState<NewOrExistingFile[]>([]);
  const [files, setFiles] = useState<NewOrExistingFile[]>([]);
  const [scheduleStart, setScheduleStart] = useState<string | null>(null);
  const [scheduleEnd, setScheduleEnd] = useState<string | null>(null);
  const [scheduleRecurrence, setScheduleRecurrence] =
    useState<Recurrence | null>(null);
  const [isCreating, setIsCreating] = useState(false);

  const { t } = useTranslation();

  const { mutateAsync: createTaskMutation, error: createTaskError } =
    useCreateTask();
  const { mutateAsync: createScheduleMutation, error: createScheduleError } =
    useCreateSchedule();
  const { data: taskTypes } = useAllTaskTypes();

  const createInitialTask = useCreateInitialTask();

  const handleTaskTypeIdChange = useEffectEvent(
    (taskTypeId: string, taskTypes: TaskType[]) => {
      setIsCreating(false);
      const taskType = taskTypes.find(({ id }) => id === taskTypeId);
      setTaskTypeNotFound(!taskType);
      if (taskType) {
        setTaskType(taskType);
        setTask(createInitialTask(taskType));
        setShowValidation(false);
        setImages([]);
        setFiles([]);
        setScheduleStart("");
        setScheduleEnd("");
        setScheduleRecurrence(null);
      }
    }
  );

  useEffect(() => {
    if (taskTypeId && taskTypes) {
      handleTaskTypeIdChange(taskTypeId, taskTypes);
    }
  }, [taskTypeId, taskTypes]);

  const { enableSaveGuard, disableSaveGuard } = useSaveGuard();

  const creatorFragmentRefs = useRef<Map<string, CreatorFragmentHandle>>(
    new Map()
  );

  const setCreatorFragmentRef = useCallback(
    (key: string) => (element: CreatorFragmentHandle | null) => {
      if (element) {
        creatorFragmentRefs.current.set(key, element);
      } else {
        creatorFragmentRefs.current.delete(key);
      }
    },
    []
  );

  const {
    ready: imageUploaderIsReady,
    upload: uploadImages,
    createUploads: imagesCreateUploads,
    error: imageUploaderError,
  } = useImageUploader(images);

  const {
    ready: fileUploaderIsReady,
    upload: uploadFiles,
    createUploads: filesCreateUploads,
    error: fileUploaderError,
  } = useFileUploader(files);

  const queryClient = useQueryClient();

  const handleMutationResults = useCallback(
    (
      createdTaskResult: PromiseSettledResult<Task>,
      imageUploadResult: PromiseSettledResult<void>,
      fileUploadResult: PromiseSettledResult<void>
    ) => {
      handleImageUploadResult(imageUploadResult, createdTaskResult);
      handleFileUploadResult(fileUploadResult, createdTaskResult);
      if (createdTaskResult.status === "fulfilled") {
        invalidateTaskQueries(queryClient);
        disableSaveGuard();
        setSearchParams((prev) => {
          prev.delete("taskCreator");
          prev.set("taskViewer", String(createdTaskResult.value["id"]));
        });
      }
    },
    [disableSaveGuard, queryClient, setSearchParams]
  );

  const createTask = useCallback(() => {
    async function fn() {
      const createTask = {
        ...task,
        images: imagesCreateUploads,
        files: filesCreateUploads,
      } as CreateTask;
      const [createdTaskResult, imageUploadResult, fileUploadResult] =
        await Promise.allSettled([
          createTaskMutation(createTask),
          uploadImages(),
          uploadFiles(),
        ]);
      handleMutationResults(
        createdTaskResult,
        imageUploadResult,
        fileUploadResult
      );
    }

    fn();
  }, [
    createTaskMutation,
    filesCreateUploads,
    handleMutationResults,
    imagesCreateUploads,
    task,
    uploadFiles,
    uploadImages,
  ]);

  const createSchedule = useCallback(() => {
    async function fn() {
      const {
        priority,
        details,
        task_type_id,
        creator_user_id,
        assigned_user_id,
        assigned_team_id,
        store_id,
      } = task as CreateTask;
      const createSchedule: CreateSchedule = {
        start: scheduleStart!,
        end: scheduleEnd!,
        recurrence: scheduleRecurrence!,
        task: {
          priority,
          details,
          task_type_id,
          creator_user_id,
          assigned_user_id,
          assigned_team_id,
          store_id,
          images: imagesCreateUploads,
          files: filesCreateUploads,
        },
      };
      const [createdTaskResult, imageUploadResult, fileUploadResult] =
        await Promise.allSettled([
          createScheduleMutation(createSchedule),
          uploadImages(),
          uploadFiles(),
        ]);
      handleMutationResults(
        createdTaskResult,
        imageUploadResult,
        fileUploadResult
      );
    }

    fn();
  }, [
    createScheduleMutation,
    filesCreateUploads,
    handleMutationResults,
    imagesCreateUploads,
    scheduleEnd,
    scheduleRecurrence,
    scheduleStart,
    task,
    uploadFiles,
    uploadImages,
  ]);

  const create = useCallback(() => {
    setIsCreating(true);
    if (scheduleRecurrence == null) {
      createTask();
    } else {
      createSchedule();
    }
  }, [createSchedule, createTask, scheduleRecurrence]);

  const handleCreateClick = useCallback(() => {
    const someFragmentsAreInvalid = creatorFragmentRefs.current
      .values()
      .some((creatorFragmentHandle) => {
        return !creatorFragmentHandle.valid;
      });

    if (someFragmentsAreInvalid) {
      setShowValidation(someFragmentsAreInvalid);
    } else {
      create();
    }
  }, [create]);

  const handleCloseClick = useCallback(() => {
    setSearchParams((prev) => {
      prev.delete("taskCreator");
    });
  }, [setSearchParams]);

  const handleTaskChange = useCallback(
    (part: Record<string, unknown>) => {
      enableSaveGuard();
      setTask((prev) => {
        if (!prev) {
          return prev;
        }
        return merge(prev, part);
      });
    },
    [enableSaveGuard]
  );

  const handleImagesChange = useCallback(
    (newImages: NewOrExistingFile[]) => {
      enableSaveGuard();
      setImages(newImages);
    },
    [enableSaveGuard]
  );

  const handleFilesChange = useCallback(
    (newFiles: NewOrExistingFile[]) => {
      enableSaveGuard();
      setFiles(newFiles);
    },
    [enableSaveGuard]
  );

  const blockInput = isCreating || !taskTypeId || !taskType;

  const creatorFragmentCommonProps: CreatorFragmentCommonProps = useMemo(() => {
    return {
      blockInput,
      task: task ? task : LOADING,
      onTaskChange: handleTaskChange,
      showValidation,
      taskType: taskType ? taskType : LOADING,
    };
  }, [blockInput, handleTaskChange, showValidation, task, taskType]);

  const handleScheduleChange = useCallback(
    ({
      futureStart,
      repeatingStart,
      end,
      recurrence,
    }: CreateTaskScheduleChangeEvent) => {
      handleTaskChange({ start: futureStart });
      setScheduleStart(repeatingStart);
      setScheduleEnd(end);
      setScheduleRecurrence(recurrence);
    },
    [handleTaskChange]
  );

  const handleRefreshClick = useCallback(() => {
    disableSaveGuard();
    setTimeout(() => {
      window.location.reload();
    }, 0);
  }, [disableSaveGuard]);

  const showSomethingWentWrongPrompt: boolean =
    !!createTaskError ||
    !!createScheduleError ||
    !!fileUploaderError ||
    !!imageUploaderError;

  const scheduleComponent = useMemo(() => {
    return (
      <CreateTaskSchedule
        disabled={blockInput}
        futureStart={(task?.["start"] || "") as string}
        onChange={handleScheduleChange}
        repeatingStart={scheduleStart}
        end={scheduleEnd}
        recurrence={scheduleRecurrence}
      />
    );
  }, [
    scheduleEnd,
    scheduleRecurrence,
    scheduleStart,
    handleScheduleChange,
    blockInput,
    task,
  ]);

  return (
    <>
      <FixaModal visible={!!taskTypeId} handleCloseBtn={handleCloseClick}>
        <FixaSheets
          labelledById="task-management-task-creator-header"
          fullSize
          className={taskTypeNotFound ? "" : styles["greyBackground"]}
          header={
            <TaskSheetHeader
              id="task-management-task-creator-header"
              onClose={handleCloseClick}
              title={
                taskTypeNotFound ? "" : t(taskType?.label ?? "createString")
              }
            />
          }
          footer={
            taskTypeNotFound ? null : (
              <TaskCreatorAndEditorSheetFooter
                text={`${t("create4String")} ${taskType?.label ? t(taskType.label) : ""}`}
                onClick={handleCreateClick}
                isDisabled={
                  !taskTypeId ||
                  !taskType ||
                  !imageUploaderIsReady ||
                  !fileUploaderIsReady
                }
                isLoading={isCreating}
              />
            )
          }
        >
          <TaskCreatorAndEditorSheetContent
            showNotFound={taskTypeNotFound}
            notFoundTitle={t("sorryTaskCantBeCreatedString")}
            notFoundMessage={t("thisTaskTypeDoesNotExistString")}
            creatorFragmentCommonProps={creatorFragmentCommonProps}
            isLoadingCreatorFragments={!taskType}
            detailsCreatorFragments={taskType ? taskType.creator_fragments : []}
            scheduleComponent={scheduleComponent}
            setCreatorFragmentRef={setCreatorFragmentRef}
            images={images}
            onImagesChange={handleImagesChange}
            isDisabled={blockInput}
            files={files}
            onFilesChange={handleFilesChange}
          />
        </FixaSheets>
      </FixaModal>
      {showSomethingWentWrongPrompt && (
        <SomethingWentWrongPrompt onRefreshClick={handleRefreshClick} />
      )}
    </>
  );
}

function handleImageUploadResult(
  imageUploadResult: PromiseSettledResult<void>,
  createdTaskResult: PromiseSettledResult<Task>
) {
  if (imageUploadResult.status === "rejected") {
    const imageUploadIds =
      createdTaskResult.status === "fulfilled"
        ? createdTaskResult.value.image_ids
        : "unknown";
    reportMessageToSentry(
      "Image upload failed",
      { uploadIds: imageUploadIds },
      "error"
    );
  }
}

function handleFileUploadResult(
  fileUploadResult: PromiseSettledResult<void>,
  createdTaskResult: PromiseSettledResult<Task>
) {
  if (fileUploadResult.status === "rejected") {
    const fileUploadIds =
      createdTaskResult.status === "fulfilled"
        ? createdTaskResult.value.file_ids
        : "unknown";
    reportMessageToSentry(
      "File upload failed",
      { uploadIds: fileUploadIds },
      "error"
    );
  }
}

export default TaskCreatorSheet;
